import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import DslEditor from './DslEditor'
import DslDocDropdown from './DslDocDropdown'
import DiagramViewer from './DiagramViewer'
import JointJSDiagramViewer from './JointJSDiagramViewer'
import ValidationPanel from './ValidationPanel'
import {
  getWorkspace,
  updateWorkspace,
  parseDsl,
  exportWorkspace,
  validateWorkspace
} from '../api/client'
import type { Workspace, Diagram, ValidationResult } from '../types'

type ExportFormat = 'native' | 'mermaid' | 'plantuml' | 'c4plantuml' | 'dot'

export default function WorkspaceEditor() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [workspace, setWorkspace] = useState<Workspace | null>(null)
  const [dsl, setDsl] = useState('')
  const [workspaceJson, setWorkspaceJson] = useState<string | null>(null)
  const [diagrams, setDiagrams] = useState<Diagram[]>([])
  const [validation, setValidation] = useState<ValidationResult | null>(null)
  const [exportFormat, setExportFormat] = useState<ExportFormat>('native')

  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [parsing, setParsing] = useState(false)
  const [, setExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasChanges, setHasChanges] = useState(false)

  // Load workspace
  useEffect(() => {
    if (!id) return

    async function load(workspaceId: string) {
      try {
        setLoading(true)
        setError(null)
        const ws = await getWorkspace(workspaceId)
        setWorkspace(ws)
        setDsl(ws.dsl || '')
        if (ws.workspace) {
          setWorkspaceJson(ws.workspace)
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load workspace')
      } finally {
        setLoading(false)
      }
    }

    load(id)
  }, [id])

  // Track changes
  const handleDslChange = useCallback((value: string) => {
    setDsl(value)
    setHasChanges(true)
  }, [])

  // Save workspace
  async function handleSave() {
    if (!id) return

    try {
      setSaving(true)
      setError(null)
      await updateWorkspace(id, { dsl })
      setHasChanges(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  // Parse DSL
  async function handleParse() {
    try {
      setParsing(true)
      setError(null)
      const result = await parseDsl(dsl)
      setWorkspaceJson(result.workspace)
      // Auto-export after successful parse (skip for native format - renders directly)
      if (exportFormat !== 'native') {
        await handleExport(result.workspace)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to parse DSL')
    } finally {
      setParsing(false)
    }
  }

  // Export diagrams
  async function handleExport(json?: string) {
    if (!id) return
    const wsJson = json || workspaceJson
    if (!wsJson) return

    try {
      setExporting(true)
      setError(null)
      const result = await exportWorkspace(id, exportFormat, wsJson)
      setDiagrams(result.diagrams)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export')
    } finally {
      setExporting(false)
    }
  }

  // Validate workspace
  async function handleValidate() {
    if (!id || !workspaceJson) return

    try {
      setError(null)
      const result = await validateWorkspace(id, workspaceJson)
      setValidation(result)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to validate')
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!workspace) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Workspace not found</p>
        <button
          onClick={() => navigate('/')}
          className="mt-4 text-primary-600 hover:text-primary-700"
        >
          Back to workspaces
        </button>
      </div>
    )
  }

  return (
    <div className="h-[calc(100vh-10rem)]">
      {/* Toolbar */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/')}
            className="text-gray-600 hover:text-gray-900"
          >
            ← Back
          </button>
          <h1 className="text-xl font-bold text-gray-900">{workspace.name}</h1>
          {hasChanges && (
            <span className="text-sm text-orange-600">Unsaved changes</span>
          )}
        </div>

        <div className="flex items-center space-x-2">
          <select
            value={exportFormat}
            onChange={e => {
              const newFormat = e.target.value as ExportFormat
              setExportFormat(newFormat)
              // Trigger export when switching to export-based formats
              if (newFormat !== 'native' && workspaceJson) {
                handleExport(workspaceJson)
              }
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="native">Native C4 (JointJS)</option>
            <option value="mermaid">Mermaid</option>
            <option value="plantuml">PlantUML</option>
            <option value="c4plantuml">C4-PlantUML</option>
            <option value="dot">Graphviz DOT</option>
          </select>

          <button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 text-sm"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>

          <button
            onClick={handleParse}
            disabled={parsing}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm"
          >
            {parsing ? 'Parsing...' : 'Parse & Preview'}
          </button>

          <button
            onClick={handleValidate}
            disabled={!workspaceJson}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 text-sm"
          >
            Validate
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {/* Editor and Preview */}
      <div className="grid grid-cols-2 gap-4 h-full">
        <div className="h-full flex flex-col">
          <div className="mb-2 flex items-center">
            <DslDocDropdown />
            <a
              href="https://docs.structurizr.com/dsl"
              target="_blank"
              rel="noopener noreferrer"
              className="ml-2 text-sm text-primary-600 hover:text-primary-700 hover:underline"
            >
              Full Documentation
            </a>
          </div>
          <div className="flex-1 min-h-0">
            <DslEditor value={dsl} onChange={handleDslChange} />
          </div>
        </div>

        <div className="h-full flex flex-col space-y-4">
          <div className="flex-1 bg-white rounded-lg shadow overflow-hidden">
            {exportFormat === 'native' ? (
              workspaceJson ? (
                <JointJSDiagramViewer workspaceJson={workspaceJson} />
              ) : (
                <div className="h-full flex items-center justify-center text-gray-500">
                  No diagrams to display. Parse your DSL first.
                </div>
              )
            ) : (
              <DiagramViewer diagrams={diagrams} format={exportFormat} />
            )}
          </div>

          {validation && (
            <div className="h-48 bg-white rounded-lg shadow overflow-hidden">
              <ValidationPanel validation={validation} onClose={() => setValidation(null)} />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
